import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { addDays, fmtDate, peso, todayStr } from "@/lib/format";
import { to } from "@/lib/urlfilters";
import { CaretRight } from "@phosphor-icons/react";
import { DataTable } from "@/components/DataTable";
import { PageHeader } from "@/components/PageHeader";
import { StatusBadge } from "@/components/StatusBadge";

/**
 * ⚠️ A TILE THAT DOES NOT DRILL IS A DEAD END. Every figure here is a count of
 * records, and the only question anybody has on reading one is "which ones?".
 * A tile that says 3 open shifts and cannot show them makes the reader go and
 * find them by hand — which is the work the tile was meant to have done.
 *
 * `to` carries the filter, so the destination opens already filtered rather
 * than merely nearby: landing on an unfiltered Schedule is a hint, not a
 * drill-through.
 */
function StatCard({ label, value, sub, tone, testid, to, title }) {
  const body = (
    <>
      <div className="flex items-start justify-between gap-2">
        <div className="text-[11px] uppercase tracking-[0.1em] font-bold text-muted-foreground">{label}</div>
        {to && <CaretRight size={13} className="shrink-0 mt-0.5 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />}
      </div>
      <div className={`font-mono text-3xl font-bold mt-2 tracking-tight ${tone || ""}`}>{value}</div>
      {sub && <div className="text-xs text-muted-foreground mt-1">{sub}</div>}
    </>
  );
  if (!to) {
    return <div className="border border-border bg-card p-4" data-testid={testid}>{body}</div>;
  }
  return (
    <Link to={to} data-testid={testid} title={title || `Open the records behind ${label}`}
      className="group border border-border bg-card p-4 block transition-colors hover:border-[hsl(var(--warning))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--warning))]">
      {body}
    </Link>
  );
}

export default function Dashboard() {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/dashboard/stats").then((r) => setStats(r.data)).catch(() => {});
  }, []);

  if (!stats) return <div className="font-mono text-xs text-muted-foreground" data-testid="dashboard-loading">LOADING…</div>;

  const cov = stats.coverage;
  const pct = cov.required ? Math.min(100, Math.round((cov.filled / cov.required) * 100)) : 100;

  return (
    <div data-testid="dashboard-page">
      <PageHeader title="Operations Dashboard" sub={`Coverage for ${fmtDate(cov.date)} · ${cov.day_kind} day`} />
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <StatCard testid="stat-coverage" label="Today's coverage"
          to={to("/schedule", { from: cov.date, to: cov.date })} value={`${cov.filled}/${cov.required}`}
          sub={cov.required ? `${pct}% of required headcount posted` : "No posts required today"}
          tone={cov.filled >= cov.required ? "text-[hsl(var(--success))]" : "text-[hsl(var(--alert))]"} />
        <StatCard testid="stat-open-shifts" label="Open shifts (7d)"
          to={to("/schedule", { status: "open", from: todayStr(), to: addDays(todayStr(), 7) })} value={stats.open_shifts_7d}
          sub={`${stats.unacknowledged_7d} published unacknowledged`}
          tone={stats.open_shifts_7d > 0 ? "text-[hsl(var(--warning))]" : ""} />
        {/*
          The grace period is named on the tile. Without it "0 late" and "7 late"
          are both unreadable — a supervisor cannot tell whether the count means
          anybody was actually late or the threshold is one second past the hour.
          It is set in Settings, so the tile must say which number it used.
        */}
        <StatCard testid="stat-late" label="Late today"
          to={to("/attendance", { from: todayStr(), to: todayStr(), only: "late" })} value={stats.late_today}
          sub={`past ${stats.late_grace_minutes ?? 15} min grace · ${stats.missed_7d} missed in last 7 days`}
          tone={stats.late_today + stats.missed_7d > 0 ? "text-[hsl(var(--alert))]" : ""} />
        <StatCard testid="stat-incidents" label="Open incidents"
          to={to("/incidents", { status: "open" })} value={stats.incidents_open}
          sub={`${stats.incidents_critical} high/critical`}
          tone={stats.incidents_critical > 0 ? "text-[hsl(var(--alert))]" : ""} />
        <StatCard testid="stat-guards" label="Deployable guards"
          to={to("/guards", { view: "deployable" })} value={`${stats.guards_deployable}/${stats.guards_total}`}
          sub={`${stats.sites_total} sites · ${stats.clients_total} clients`} />
        <StatCard testid="stat-expiring" label="Docs expiring ≤30d"
          to={to("/guards", { view: "expiring" })} value={stats.expiring_docs_count}
          sub="Mandatory expiry blocks assignment"
          tone={stats.expiring_docs_count > 0 ? "text-[hsl(var(--warning))]" : ""} />
        <StatCard testid="stat-reserved" label="On reserved status"
          to="/reserved"
          value={stats.reserved_status?.total_floating ?? 0}
          sub={stats.reserved_status?.breach
            ? `${stats.reserved_status.breach} PAST SIX MONTHS`
            : stats.reserved_status?.soonest_breach
              ? `earliest six-month date ${stats.reserved_status.soonest_breach}`
              : "everybody has a post or one booked"}
          tone={stats.reserved_status?.breach ? "text-[hsl(var(--alert))]"
            : (stats.reserved_status?.critical || stats.reserved_status?.warning) ? "text-[hsl(var(--warning))]" : ""} />
        <StatCard testid="stat-outstanding" label="Outstanding invoices"
          to={to("/invoices", { status: "outstanding" })} value={peso(stats.invoices_outstanding)} sub="Approved / delivered / partial" />
        {/*
          A draft is neither billed nor outstanding, so it appeared in no figure
          on this screen — six of them, worth more than the whole outstanding
          total, the oldest a week old. The client has not been asked to pay and
          nobody is chasing it.
        */}
        <StatCard testid="stat-drafts" label="Invoices not yet sent"
          to={to("/invoices", { status: "draft" })}
          value={peso(stats.invoices_draft_total)}
          sub={stats.invoices_draft
            ? `${stats.invoices_draft} draft(s), oldest ${stats.invoices_draft_oldest} — not billed, not chased`
            : "nothing sitting in draft"}
          tone={stats.invoices_draft > 0 ? "text-[hsl(var(--warning))]" : ""} />
        {/*
          ⚠️ SEPARATE FROM "OPEN SHIFTS", deliberately. That tile counts from
          today forward — work still to do. This one counts posts whose hour
          came and went with nobody rostered: the client did not get the guard
          they are paying for. Folding them into one number would make it mean
          two jobs at once, for two different people, and a number like that
          gets acted on by neither.
        */}
        <StatCard testid="stat-unstood" label="Posts nobody stood (30d)"
          to={to("/schedule", { status: "open", from: stats.posts_nobody_stood_from, to: addDays(todayStr(), -1) })}
          value={stats.posts_nobody_stood ?? 0}
          sub={stats.posts_nobody_stood
            ? `oldest ${stats.posts_nobody_stood_oldest} — unfilled when the hour came`
            : "every rostered post was covered"}
          tone={stats.posts_nobody_stood > 0 ? "text-[hsl(var(--alert))]" : ""} />
        <StatCard testid="stat-payroll" label="Latest payroll period"
          to={to("/payroll", { period: stats.payroll_latest?.id })}
          value={stats.payroll_latest ? <StatusBadge value={stats.payroll_latest.status} /> : "—"}
          sub={stats.payroll_latest ? `${stats.payroll_latest.name} (${stats.payroll_latest.start_date} → ${stats.payroll_latest.end_date})` : "No period created yet"} />
      </div>

      {(stats.reserved_status?.breach > 0 || stats.reserved_status?.critical > 0) && (
        <Link to="/reserved" className="block border border-[hsl(var(--alert))] bg-[hsl(var(--alert))]/10 p-3 mb-6"
              data-testid="dashboard-reserved-band">
          <div className="text-xs leading-snug">
            <span className="font-bold">
              {stats.reserved_status.breach > 0
                ? `${stats.reserved_status.breach} guard(s) have been off-detail for more than six months.`
                : `${stats.reserved_status.critical} guard(s) are within a month of six months off-detail.`}
            </span>{" "}
            Six months is constructive dismissal under DO 150-16 — backwages and separation pay, per guard.
            Repost them, offer a relocation, or separate them with pay.{" "}
            <span className="underline font-semibold">Open Reserved Status</span>
          </div>
        </Link>
      )}

      <div className="border border-border bg-card p-4 mb-6" data-testid="coverage-bar">
        <div className="text-[11px] uppercase tracking-[0.1em] font-bold text-muted-foreground mb-2">Coverage ratio</div>
        <div className="h-3 bg-secondary border border-border">
          <div className={`h-full transition-[width] duration-300 ${pct >= 100 ? "bg-[hsl(var(--success))]" : "bg-[hsl(var(--warning))]"}`} style={{ width: `${pct}%` }} />
        </div>
      </div>

      <h2 className="font-heading text-lg font-bold mb-3">Document expiries (next 30 days)</h2>
      <DataTable
        testid="expiring-docs-table"
        empty="No documents expiring soon"
        columns={[
          { key: "guard", label: "Guard", render: (r) => <div><div className="font-semibold">{r.guard_name}</div><div className="font-mono text-[10px] text-muted-foreground">{r.employee_no}</div></div> },
          { key: "type", label: "Document" },
          { key: "number", label: "Number", render: (r) => <span className="font-mono text-xs">{r.number}</span> },
          { key: "expiry", label: "Expiry", render: (r) => <span className="font-mono text-xs">{r.expiry}</span> },
          { key: "state", label: "State", render: (r) => r.expired ? <StatusBadge value="missed" testid="doc-expired" /> : <StatusBadge value="pending" /> },
          { key: "required", label: "Blocking", render: (r) => (r.required ? "Yes" : "No") },
        ]}
        rows={stats.expiring_docs}
      />
      <div className="mt-6 flex gap-4 font-mono text-xs">
        <Link data-testid="dash-goto-schedule" className="underline underline-offset-4 hover:text-[hsl(var(--warning))]" to="/schedule">Open schedule →</Link>
        <Link data-testid="dash-goto-attendance" className="underline underline-offset-4 hover:text-[hsl(var(--warning))]" to="/attendance">Attendance review →</Link>
      </div>
    </div>
  );
}
